import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { toastError, toastSuccess } from 'utils/toastify'
import { errorMessage, videStatusConst } from 'constant/const'
import axiosInstance from 'utils/axiosConfig'

const initialState = {
   allPostData: [],
   postDetails: null,
   postList: null,
   loading: false,
   isNextPage: null,
   isAllPostGet: false,
   totalPostsCount: 0,
   inDashboardData: null,
   getTopPostsList: [],
   getTopPostLoader: false,
   instagramChartData: null,
   instagramChartDataLoader: false,
   commentList: null,
   totalCommentsCount: 0,
   isNextCommentPage: false,
   loadingComment: false,
   loadMoreCommentsLoading: false,
   postLineChartData: null,
   postLineChartDataLoader: false,
   isBatchAnalysisStatus: false,
   selectedVideoIds: [],
   pageNumber: 1,
   loadMoreComments: false,
   updateInCommentLoading: false,
   updateInCommentStatus: false,
   isBackToPost: false,
   postDetailsLoading: false
}

export const getPostListAction = createAsyncThunk(
   'InstagramDashboard/getPostListAction',
   async (data) => {
      try {
         const params = new URLSearchParams(data).toString()
         const res = await axiosInstance.get(`/instagram/posts/?${params}`)
         if (data?.fetch) {
            return { ...res.data, fetch: true }
         } else {
            return res.data
         }
      } catch (error) {
         toastError(errorMessage)
         throw error.response ? error.response?.data?.message : error?.message
      }
   }
)

export const getDashboardAction = createAsyncThunk(
   'InstagramDashboard/getDashboardAction',
   async () => {
      try {
         const res = await axiosInstance.get(`/instagram/dashboard/`)
         return res.data?.data
      } catch (error) {
         toastError(errorMessage)
         throw error.response ? error.response?.data?.message : error?.message
      }
   }
)

export const getPostDetailsAction = createAsyncThunk(
   'InstagramDashboard/getPostDetailsAction',
   async (data) => {
      try {
         const params = new URLSearchParams(data).toString()
         const res = await axiosInstance.get(`/instagram/post/?${params}`)
         if (res.data?.data?.status === videStatusConst.processing) {
            toastSuccess(
               `Analyzing comments for ${res.data?.data?.title}. This may take a moment due to a high number of comments.`
            )
         }
         return res.data?.data
      } catch (error) {
         toastError(errorMessage)
         throw error.response ? error.response?.data?.message : error?.message
      }
   }
)

export const getTopPostsListAction = createAsyncThunk(
   'InstagramDashboard/getTopPostsListAction',
   async () => {
      try {
         const res = await axiosInstance.get(`/instagram/get_top_posts_list/`)
         return res.data?.data
      } catch (error) {
         toastError(errorMessage)
         throw error.response ? error.response?.data?.message : error?.message
      }
   }
)

export const getInstagramChartDataAction = createAsyncThunk(
   'InstagramDashboard/getInstagramChartDataAction',
   async (data) => {
      try {
         const params = new URLSearchParams(data).toString()
         const res = await axiosInstance.get(`/instagram/dashboard/chart_data/?${params}`)
         return res.data?.data
      } catch (error) {
         toastError(errorMessage)
         throw error.response ? error.response?.data?.message : error?.message
      }
   }
)

export const getPostCommentListAction = createAsyncThunk(
   'InstagramDashboard/getPostCommentListAction',
   async (data) => {
      try {
         const params = new URLSearchParams(data).toString()
         const res = await axiosInstance.get(`/instagram/get_comments_list/?${params}`)
         return res.data
      } catch (error) {
         toastError(errorMessage)
         throw error.response ? error.response?.data?.message : error?.message
      }
   }
)

export const getPostLineChartAction = createAsyncThunk(
   'InstagramDashboard/getPostLineChartAction',
   async (data) => {
      try {
         const res = await axiosInstance.get(
            `/instagram/get_chart_data_by_post_id/?id=${data?.id}&period_type=${data?.period_type}`
         )
         return res.data?.data
      } catch (error) {
         toastError(errorMessage)
         throw error.response ? error.response?.data?.message : error?.message
      }
   }
)

export const loadMoreInstCommentsAction = createAsyncThunk(
   'InstagramDashboard/loadMoreInstCommentsAction',
   async (data) => {
      try {
         const res = await axiosInstance.get(
            `/instagram/get_more_comments/?post_id=${data?.post_id}&offset=${data?.offset}`
         )
         return res.data?.data
      } catch (error) {
         toastError(errorMessage)
         throw error.response ? error.response?.data?.message : error?.message
      }
   }
)

export const updateInCommentsAction = createAsyncThunk(
   'InstagramDashboard/updateInCommentsAction',
   async (data) => {
      try {
         const res = await axiosInstance.post(`/instagram/user_update_comment/`, data)
         return res.data?.data
      } catch (error) {
         toastError(errorMessage)
         throw error.response ? error.response?.data?.message : error?.message
      }
   }
)

const InstagramDashboardSlice = createSlice({
   name: 'InstagramDashboard',
   initialState,
   reducers: {
      resetFetchAllPostStatus(state) {
         state.isAllPostGet = false
      },
      resetTotalPostsCount(state) {
         state.totalPostsCount = 0
         state.allPostData = []
      },
      setInstagramBatchAnalysisStatus(state, action) {
         state.isBatchAnalysisStatus = action.payload
      },
      toggleInstagramVideoSelection: (state, action) => {
         const videoId = action.payload
         if (state.selectedVideoIds.includes(videoId)) {
            state.selectedVideoIds = state.selectedVideoIds.filter((id) => id !== videoId)
         } else {
            state.selectedVideoIds.push(videoId)
         }
      },
      clearInstagramAllSelections: (state) => {
         state.selectedVideoIds = []
      },
      setAllPostDataAction(state, action) {
         state.allPostData = action.payload
      },
      setInstagramPageNumberAction(state, action) {
         state.pageNumber = action.payload
      },
      resetUpdateInCommentStatus(state) {
         state.updateInCommentStatus = false
      },
      resetIsBackToPost(state) {
         state.isBackToPost = false
      }
   },
   extraReducers: (builder) => {
      builder
         .addCase(getPostListAction.pending, (state) => {
            state.loading = true
            state.postDetails = null
            state.isAllPostGet = false
            state.totalPostsCount = 0
            state.totalCommentsCount = 0
            state.commentList = null
            state.loadMoreComments = false
            state.isBackToPost = false
         })
         .addCase(getPostListAction.fulfilled, (state, action) => {
            state.loading = false
            state.postList = action.payload?.data
            state.isNextPage = action.payload?.next
            state.totalPostsCount = action.payload?.count
            state.isBackToPost = false
            if (action.payload?.fetch) {
               state.isAllPostGet = true
            } else {
               state.isAllPostGet = false
            }
         })
         .addCase(getPostListAction.rejected, (state) => {
            state.loading = false
            state.postList = null
            state.isAllPostGet = false
            state.totalPostsCount = 0
            state.isBackToPost = false
         })

         .addCase(getPostDetailsAction.pending, (state) => {
            state.postDetailsLoading = true
            state.postDetails = null
            state.isAllPostGet = false
            state.postList = null
            state.postLineChartData = null
            state.commentList = []
            state.loadMoreComments = false
         })
         .addCase(getPostDetailsAction.fulfilled, (state, action) => {
            state.postDetailsLoading = false
            if (action?.payload?.status === videStatusConst.processing) {
               state.isBackToPost = true
            } else {
               state.isBackToPost = false
               state.postDetails = action.payload
            }
         })
         .addCase(getPostDetailsAction.rejected, (state) => {
            state.postDetailsLoading = false
            state.postDetails = null
            state.isBackToPost = false
         })
         .addCase(getDashboardAction.pending, (state) => {
            state.loading = true
            state.inDashboardData = null
            state.totalCommentsCount = 0
            state.commentList = null
         })
         .addCase(getDashboardAction.fulfilled, (state, action) => {
            state.loading = false
            state.inDashboardData = action.payload
            state.isBackToPost = false
         })
         .addCase(getDashboardAction.rejected, (state) => {
            state.loading = false
            state.inDashboardData = null
         })
         .addCase(getTopPostsListAction.pending, (state) => {
            state.getTopPostLoader = true
            state.getTopPostsList = []
         })
         .addCase(getTopPostsListAction.fulfilled, (state, action) => {
            state.getTopPostLoader = false
            state.getTopPostsList = action.payload
         })
         .addCase(getTopPostsListAction.rejected, (state) => {
            state.getTopPostLoader = false
            state.getTopPostsList = []
         })
         .addCase(getInstagramChartDataAction.pending, (state) => {
            state.instagramChartDataLoader = true
            state.instagramChartData = null
         })
         .addCase(getInstagramChartDataAction.fulfilled, (state, action) => {
            state.instagramChartDataLoader = false
            state.instagramChartData = action.payload
         })
         .addCase(getInstagramChartDataAction.rejected, (state) => {
            state.instagramChartDataLoader = false
            state.instagramChartData = []
         })
         .addCase(getPostCommentListAction.pending, (state) => {
            state.loadingComment = true
            // state.commentList = []
            state.totalCommentsCount = 0
         })
         .addCase(getPostCommentListAction.fulfilled, (state, action) => {
            state.loadingComment = false
            state.commentList = action.payload?.data
            state.totalCommentsCount = action.payload?.count
            state.isNextCommentPage = action.payload?.next
         })
         .addCase(getPostCommentListAction.rejected, (state) => {
            state.loadingComment = false
            state.commentList = null
            state.totalCommentsCount = 0
            state.isNextCommentPage = null
         })
         .addCase(getPostLineChartAction.pending, (state) => {
            state.postLineChartDataLoader = true
            state.postLineChartData = null
         })
         .addCase(getPostLineChartAction.fulfilled, (state, action) => {
            state.postLineChartDataLoader = false
            state.postLineChartData = action.payload
         })
         .addCase(getPostLineChartAction.rejected, (state) => {
            state.postLineChartDataLoader = false
            state.postLineChartData = null
         })
         .addCase(loadMoreInstCommentsAction.pending, (state) => {
            state.loadMoreCommentsLoading = true
            state.loadMoreComments = false
         })
         .addCase(loadMoreInstCommentsAction.fulfilled, (state) => {
            state.loadMoreCommentsLoading = false
            state.loadMoreComments = true
            state.videoRiskScore = null
         })
         .addCase(loadMoreInstCommentsAction.rejected, (state) => {
            state.loadMoreCommentsLoading = false
            state.loadMoreComments = false
         })
         .addCase(updateInCommentsAction.pending, (state) => {
            state.updateInCommentLoading = true
            state.updateInCommentStatus = false
         })
         .addCase(updateInCommentsAction.fulfilled, (state, action) => {
            state.updateInCommentLoading = false
            state.postDetails = action.payload
            state.updateInCommentStatus = true
         })
         .addCase(updateInCommentsAction.rejected, (state) => {
            state.updateInCommentLoading = false
            state.updateInCommentStatus = false
         })
   }
})

export const {
   resetFetchAllPostStatus,
   resetTotalPostsCount,
   setInstagramBatchAnalysisStatus,
   toggleInstagramVideoSelection,
   clearInstagramAllSelections,
   setAllPostDataAction,
   setInstagramPageNumberAction,
   resetUpdateInCommentStatus,
   resetIsBackToPost
} = InstagramDashboardSlice.actions
export default InstagramDashboardSlice.reducer
